import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { AccountAvailableColumns, useGetAllAccounts } from '../services/react-query/accounts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { AccountFilters, AccountColumns } from '../utility/account';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import { Account } from '../types/Account';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import PreferencePill from '../components/preferences/PreferencePill';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { FilterState } from '../components/common/filters/interfaces';
import _ from 'lodash';
import { useDownloadCsv } from '../hooks/useDownloadCsv';

const AccountsList: React.FC = () => {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const { userSorts } = useUserSorts(UserConfigurableModule.ACCOUNTS);
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [filterState, setFilterState] = useState<FilterState>(undefined);

  const { data: prefs } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.ACCOUNT_TYPE, PREFERENCES_VALID_KEYS.ACCOUNT_STATUS, PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT],
      },
    },
  });

  const prefByUuid = useMemo(() => _.keyBy(prefs ?? [], 'uuid'), [prefs]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState || {},
    sort: userSorts,
  };

  const { data, isLoading: acctLoading } = useGetAllAccounts({
    params: {
      ...baseParams,
      page: currentPage,
    },
    enabled: true,
  });

  const pillColumns = useMemo(() => {
    return AccountColumns.map<AvailableColumn<Account, AccountAvailableColumns>>((col) => {
      if (col.key === 'status_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.status_uuid ?? '']} />,
        };
      }
      if (col.key === 'type_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.type_uuid ?? '']} />,
        };
      }
      if (col.key === 'school_district_value') {
        return {
          ...col,
          render: (row: Account) => <PreferencePill {...prefByUuid[row.school_district_uuid ?? '']} />,
        };
      }
      return col;
    });
  }, [prefByUuid]);

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: 'accounts',
  });

  if (acctLoading) return <Loading />;

  return (
    <Box>
      <ListHeader
        title="Accounts"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={AccountFilters(prefs)}
            open={sidebarOpen}
            module={UserConfigurableModule.ACCOUNTS}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Account, AccountAvailableColumns>
            availableColumns={pillColumns}
            data={data?.data || []}
            module={UserConfigurableModule.ACCOUNTS}
            page={currentPage}
            count={data?.meta?.total_pages ?? 1}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AccountsList;
